import { useCallback, useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";

export function PronunciationPanel({ book, onChange }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ term: "", spoken: "" });
  const load = useCallback(() => api.get(`/books/${book.id}/pronunciations`).then((r) => setList(r.data)), [book.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { try { await api.post(`/books/${book.id}/pronunciations`, f); setF({ term: "", spoken: "" }); load(); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (id) => { await api.delete(`/pronunciations/${id}`); load(); onChange(); };
  return (
    <div className="panel p-5 space-y-3" data-testid="pronunciation-panel">
      <p className="eyebrow">Pronunciation dictionary</p>
      <p className="text-[11px] text-slate-500">Names and invented words are spoken the way you spell them here. Applied to every narration script; your manuscript is never changed.</p>
      <div className="flex gap-2">
        <Input placeholder="As written (e.g. Aoife)" value={f.term} onChange={(e) => setF({ ...f, term: e.target.value })} className="h-8" data-testid="pron-term-input" />
        <Input placeholder="Say it as (e.g. Ee-fa)" value={f.spoken} onChange={(e) => setF({ ...f, spoken: e.target.value })} className="h-8" data-testid="pron-spoken-input" />
        <Button size="sm" onClick={add} disabled={!f.term || !f.spoken} className="bg-amber-600 h-8" data-testid="pron-add-button"><Plus size={13} /></Button>
      </div>
      <div className="max-h-44 overflow-y-auto scrollbar-thin">
        {list.map((p) => <div key={p.id} className="flex justify-between items-center text-sm py-1 border-b border-slate-800/60" data-testid={`pron-row-${p.id}`}><span><b className="text-slate-200">{p.term}</b> <span className="text-slate-500">→</span> <span className="text-amber-300">{p.spoken}</span></span><button onClick={() => del(p.id)} className="text-slate-500 hover:text-rose-400" data-testid={`pron-delete-${p.id}`}><Trash2 size={13} /></button></div>)}
        {!list.length && <p className="text-xs text-slate-500">No entries yet.</p>}
      </div>
    </div>
  );
}

export function ScriptPreview({ book, refresh }) {
  const [d, setD] = useState(null);
  const [i, setI] = useState(0);
  useEffect(() => { if (book.approved_version_id) api.get(`/books/${book.id}/narration-scripts`).then((r) => setD(r.data)).catch(() => {}); }, [book.id, book.approved_version_id, refresh]);
  if (!d?.scripts.length) return null;
  const s = d.scripts[Math.min(i, d.scripts.length - 1)];
  const applied = Object.entries(s.applied);
  return (
    <div className="panel p-5 space-y-2" data-testid="script-preview">
      <div className="flex justify-between items-center gap-2"><p className="eyebrow">Narration script · from v{d.version_number}</p>
        <select value={i} onChange={(e) => setI(Number(e.target.value))} className="h-8 rounded-md bg-slate-900 border border-slate-700 px-2 text-xs max-w-[220px]" data-testid="script-chapter-select">{d.scripts.map((x, n) => <option key={x.chapter_id} value={n}>{x.title}</option>)}</select></div>
      <p className="text-[11px] font-mono text-slate-500" data-testid="script-meta">{s.characters.toLocaleString()} characters · script id {s.sha256.slice(0, 10)}{applied.length ? ` · ${applied.map(([k, n]) => `${k}×${n}`).join(", ")} re-spelled` : ""}</p>
      <pre className="whitespace-pre-wrap text-xs text-slate-300 max-h-56 overflow-y-auto scrollbar-thin font-sans" data-testid="script-text">{s.script.slice(0, 4000)}{s.script.length > 4000 ? "…" : ""}</pre>
    </div>
  );
}
